import { Injectable } from '@angular/core'
import { catchError, tap } from 'rxjs/operators'
import { throwError, Observable } from 'rxjs'
import { DocumentNode } from 'graphql'
import { TypedDocumentNode } from '@apollo/client/core'
import { MutationUpdaterFn, WatchQueryFetchPolicy } from '@apollo/client/core'
import { DataService } from './data.service'
import { LocalStorageService } from '../../providers/local-storage/local-storage.service'
import { AnalyticService } from './analytic.service'
import { BaseDataService } from './base-data.service'



@Injectable({
  providedIn: 'root'})
export class LoggingDataService extends DataService {
  constructor(
    baseDataService: BaseDataService,
    private analytic: AnalyticService,
    private storage: LocalStorageService
  ) {
    super(baseDataService)
    console.log(
      '[LoggingDataService] Initialized - All GraphQL operations will be logged'
    )
  }

  /**
   * Xử lý lỗi GraphQL và log vào analytics
   */
  private async handleGraphQLError(
    type: 'query' | 'mutation',
    error: any,
    startTime: number,
    operationName?: string
  ): Promise<void> {
    const duration = (Date.now() - startTime) / 1000
    const userProfile = (this.storage.get('userProfile') || {}) as {
      id?: string
      email?: string
    }
    const username = userProfile?.id || userProfile?.email || 'ADMIN'

    // TODO: Use for debug
    // console.error(`[GraphQL ${type.toUpperCase()} ERROR]:`, {
    //   operation: operationName,
    //   error,
    //   duration: `${duration}s`
    // })

    await this.analytic.logError(`${type}_${operationName}`, error, {
      duration_seconds: duration,
      username,
      message: error?.message || error?.toString() || 'Không xác định',
      stack: error?.stack || ''
    })
  }

  /**
   * Lấy tên operation từ DocumentNode (nếu có)
   */
  private getOperationName(
    query: DocumentNode | TypedDocumentNode<any, any>
  ): string {
    try {
      const definition = query.definitions?.[0]
      if (definition && 'name' in definition && definition.name) {
        return definition.name.value
      }
    } catch (e) {
      // Ignore
    }
    return 'unknown_operation'
  }

  /**
   * Override query để bắt lỗi trong single$ / stream$
   */
  override query<T, V extends Record<string, any> = Record<string, any>>(
    query: DocumentNode | TypedDocumentNode<T, V>,
    variables?: V,
    fetchPolicy?: WatchQueryFetchPolicy
  ) {
    const start = Date.now()
    const operationName = this.getOperationName(query)

    // Lấy QueryResult gốc
    const result = super.query<T, V>(query, variables, fetchPolicy)

    // Patch single$ observable
    const originalSingle$ = result.single$
    Object.defineProperty(result, 'single$', {
      get: () =>
        originalSingle$.pipe(
          tap(() => {
            // TODO: Use for debug
            const duration = (Date.now() - start) / 1000
            // console.log(
            //   `[GraphQL QUERY] ${operationName} completed in ${duration}s`
            // )
          }),
          catchError((err) => {
            void this.handleGraphQLError('query', err, start, operationName)
            return throwError(() => err)
          })
        )
    })

    // Patch stream$ observable
    const originalStream$ = result.stream$
    Object.defineProperty(result, 'stream$', {
      get: () =>
        originalStream$.pipe(
          tap(() => {
            // TODO: Use for debug
            const duration = (Date.now() - start) / 1000
            // console.log(
            //   `[GraphQL QUERY STREAM] ${operationName} emitted data in ${duration}s`
            // )
          }),
          catchError((err) => {
            void this.handleGraphQLError('query', err, start, operationName)
            return throwError(() => err)
          })
        )
    })

    return result
  }

  /**
   * Override mutate để bắt lỗi mutation
   */
  override mutate<T, V extends Record<string, any> = Record<string, any>>(
    mutation: DocumentNode | TypedDocumentNode<T, V>,
    variables?: V,
    update?: MutationUpdaterFn<T>
  ): Observable<T> {
    const start = Date.now()
    const operationName = this.getOperationName(mutation)

    return super.mutate<T, V>(mutation, variables, update).pipe(
      tap(() => {
        // TODO: Use for debug
        const duration = (Date.now() - start) / 1000
        // console.log(
        //   `[GraphQL MUTATION] ${operationName} completed in ${duration}s`
        // )
      }),
      catchError((err) => {
        void this.handleGraphQLError('mutation', err, start, operationName)
        return throwError(() => err)
      })
    )
  }
}
